<!-- File Upload card -->
<template>
	<div class="card">
		<div class="card-body">
			<h4 class="header-title mb-2">{{ $t('Place Order.heading') }}</h4>

			<!-- THE TOP HALF OF THE CARD: -->
			<!-- file upload dropzone: -->
			<template v-if="uiState !== 'SUBMIT_SUCCESS'">
				<br />
				<div class="d-flex flex-wrap">
					<div
						v-if="
							currentUser &&
							!(
								currentUser.roles.includes('CLT') &&
								clients.length <= 1
							)
						"
						class="mr-3"
					>
						<!-- Clients: -->
						<div class="font-weight-bold">
							{{ $t('Place Order.label_select_client') }}
						</div>
						<b-form-select
							:value="clientId"
							@change="handleClientChange"
						>
							<b-form-select-option
								v-for="client of clients"
								:key="client.id"
								:value="client.id"
							>
								{{
									getTranslationValue(
										clients,
										client.id,
										'name'
									)
								}}
							</b-form-select-option>
						</b-form-select>
					</div>
					<div
						v-if="
							shouldShowLocationSelect &&
							(
								(clients.find(c => c.id === clientId) || {})
									.locations || []
							).length !== 1
						"
						class="mr-3"
					>
						<!-- Locations: -->
						<div class="font-weight-bold">
							{{ $t('Place Order.label_select_location') }}
						</div>
						<b-form-select
							:value="locationId"
							@change="handleLocationChange"
						>
							<b-form-select-option
								v-for="location of locations"
								:key="location.id"
								:value="location.id"
							>
								{{
									getTranslationValue(locations, location.id)
								}}
							</b-form-select-option>
						</b-form-select>
					</div>
					<div class="mr-3">
						<!-- DELIVERY STAGE: -->
						<div class="font-weight-bold">
							{{ $t('Place Order.label_select_delivery_stage') }}
						</div>
						<b-form-select
							:value="deliveryStage"
							@change="handleDeliveryStageChange"
						>
							<b-form-select-option
								v-for="stage of deliveryStages"
								:key="stage"
								:value="stage"
							>
								{{ $t(`Place Order.DELIVERY_STAGES.${stage}`) }}
							</b-form-select-option>
						</b-form-select>
					</div>
					<!-- Spreadsheet Format selection (shown only if client-specific format is available): -->
					<div v-if="formats && formats.length">
						<div class="font-weight-bold">
							{{
								$t(
									'Place Order.label_select_spreadsheet_format'
								)
							}}
						</div>
						<b-form-select
							:value="formatId"
							@change="handleFormatChange"
						>
							<b-form-select-option
								v-for="format of formats"
								:key="format.id"
								:value="format.id"
							>
								{{ format.id }}
							</b-form-select-option>
						</b-form-select>
					</div>
				</div>
				<br />
				<div class="dropzone-container">
					<vue-dropzone
						id="csv-dropzone"
						class="dropzone"
						:use-custom-slot="true"
						:include-styling="false"
						:options="dropzoneOptions"
						@vdropzone-file-added="handleFileAdded"
					>
						<div class="dropzone-custom-content">
							<img
								src="@/assets/images/csv.svg"
								height="80"
								alt="csv icon"
							/>
							<h3>{{ $t('Place Order.label_file_input') }}</h3>
						</div>
					</vue-dropzone>
					<!-- maybe for future use: -->
					<div class="dropzone-previews mt-3" />
					<div v-if="loading" class="dropzone-overlay" />
				</div>
				<b-progress
					v-if="loading"
					:value="100"
					height="48px"
					animated
				/>
			</template>

			<!-- if validation error: -->
			<template v-if="uiState === 'VALIDATE_ERROR'">
				<div
					v-if="!loading"
					class="alert alert-danger text-center font-weight-bold"
					role="alert"
				>
					<!-- <i class="mdi mdi-cancel mr-2" /> -->
					<span class="font-16">{{ file.name || '' }}</span>
					:
					<span class="font-16">
						{{ $t('Place Order.msg_validation_error') }}
					</span>
					<p
						v-if="spreadsheetError && spreadsheetError.feedbackCsv"
						class="my-1 font-weight-normal"
					>
						{{ $t('Place Order.label_download_feedback_start') }}
						<b>
							<a href="" @click.prevent="downloadFeedbackCsv">
								{{ $t('Place Order.label_download') }}
							</a>
						</b>
						{{ $t('Place Order.label_download_feedback_end') }}
					</p>
					<template v-if="spreadsheetError && spreadsheetError.rows">
						<p class="mt-3 mb-1 text-left">
							Details of rows with errors:
						</p>
						<ul class="py-0 px-3 text-left font-weight-normal">
							<li
								v-for="{
									row,
									message
								} of spreadsheetError.rows"
								:key="row"
							>
								<b>Row {{ row }}</b>
								- {{ message }}
							</li>
						</ul>
					</template>
				</div>
				<ApiErrorAlert v-if="!loading && error" :error="error" />
			</template>

			<!-- if validation success: -->
			<template
				v-if="
					uiState === 'VALIDATE_SUCCESS' ||
					uiState === 'SUBMIT_ERROR' ||
					uiState === 'TIMEOUT_WARNING'
				"
			>
				<div
					v-if="!loading && uiState === 'VALIDATE_SUCCESS'"
					class="alert alert-success text-center font-weight-bold"
					role="alert"
				>
					<i class="mdi mdi-checkbox-marked-circle-outline mr-2" />
					<span>{{ file.name || '' }}</span>
					:
					<span>{{ $t('Place Order.msg_validation_success') }}</span>
				</div>
				<ApiErrorAlert
					v-else-if="!loading && uiState === 'SUBMIT_ERROR'"
					:error="error"
				/>
				<div
					v-if="!loading && uiState === 'TIMEOUT_WARNING'"
					class="alert alert-warning text-center font-weight-bold"
					role="alert"
				>
					<i class="mdi mdi-checkbox-marked-circle-outline mr-2" />
					<span>{{ file.name || '' }}</span>
					:
					<span>{{ $t('Place Order.msg_timeout_warning') }}</span>
				</div>
				<div v-if="file && spreadsheetInfo" class="font-16">
					<div>
						{{ $t('Place Order.label_file_size') }}:
						{{ displayFileSize(file.size) }}
					</div>
					<div
						v-if="spreadsheetInfo.rowsProcessed"
						class="font-weight-bold"
					>
						{{ $t('Place Order.label_total_deliveries') }}:
						{{ spreadsheetInfo.rowsProcessed }}
					</div>
					<div v-else class="font-weight-bold">
						{{ 'No new or updated delivery.' }}
					</div>
					<div
						v-for="(value, key) in spreadsheetInfo.group"
						:key="key"
						class="px-2"
					>
						{{
							`${displayDatetime(
								key,
								$t('Datetime.date_full')
							)}: ${value}`
						}}
					</div>
				</div>
			</template>

			<!-- if submission success: -->
			<template v-if="uiState === 'SUBMIT_SUCCESS'">
				<div class="text-center">
					<img
						src="@/assets/images/success.png"
						height="80"
						alt="success icon"
					/>
					<h3>{{ $t('Place Order.msg_submission_success') }}</h3>
					<br />
					<p>
						<button
							type="button"
							class="btn btn-primary"
							@click="handleReset"
						>
							<i class="mdi mdi-package-variant-closed" />
							{{ 'Place More Orders' }}
						</button>
					</p>
				</div>
			</template>

			<hr />

			<!-- THE BOTTOM HALF OF THE CARD: -->
			<!-- download sample file: -->
			<template
				v-if="uiState === 'DEFAULT' || uiState === 'VALIDATE_ERROR'"
			>
				<p v-if="templateUrl" class="font-16">
					{{ $t('Place Order.label_download_template') }}
					<a :href="templateUrl" target="_blank">
						{{ $t('Place Order.label_download_template_link') }}
					</a>
					.
				</p>
			</template>

			<!-- if validation success, show preview table: -->
			<template
				v-if="
					spreadsheetData.length &&
					(uiState === 'VALIDATE_SUCCESS' ||
						uiState === 'SUBMIT_ERROR')
				"
			>
				<OrderPreview :list="spreadsheetData" />
				<div class="text-center mt-4">
					<button
						type="button"
						class="btn btn-primary btn-lg"
						:disabled="loading"
						@click="handleSubmit"
					>
						<span
							v-if="loading"
							class="spinner-border spinner-border-sm mr-1 align-middle"
							role="status"
							aria-hidden="true"
						/>
						<i v-else class="mdi mdi-cube-send" />
						<!-- TODO: update translation JSON structure.. -->
						{{
							loading
								? $t('Login.button_loading')
								: $t('Common.button_submit')
						}}
					</button>
				</div>
			</template>

			<!-- if submission success: -->
			<template v-if="uiState === 'SUBMIT_SUCCESS'">
				<p class="font-16">
					{{ $t('Place Order.label_download_uploaded') }}
					<a :href="fileUrl">
						{{
							$t(
								'Place Order.label_download_uploaded_file_name',
								{
									fileName: file.name
								}
							)
						}}
					</a>
					.
				</p>
			</template>
		</div>
	</div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import ApiErrorAlert from '@/components/ApiErrorAlert.vue'
import OrderPreview from '@/components/OrderPreview.vue'
import {
	authComputed,
	configComputed,
	configMethods,
	placeOrderComputed,
	placeOrderMethods
} from '@/store/helpers'
import { displayDatetime } from '@/utils/helpers'

export default {
	components: { vueDropzone: vue2Dropzone, ApiErrorAlert, OrderPreview },
	i18nOptions: { keyPrefix: 'PORTAL' },
	data() {
		const { VUE_APP_ENABLE_PLACE_ORDER_LOCATION } = process.env

		return {
			dropzoneOptions: {
				url: '/', // required by the lib to provide URL..
				acceptedFiles: '.csv',
				autoProcessQueue: false, // do not auto-upload file
				previewTemplate: `<div></div>`, // maybe for future use..
				previewsContainer: '.dropzone-previews'
			},
			shouldShowLocationSelect:
				VUE_APP_ENABLE_PLACE_ORDER_LOCATION === 'true'
		}
	},
	computed: {
		...authComputed,
		...configComputed,
		...placeOrderComputed
	},
	created() {
		this.$store.subscribe((mutation, state) => {
			if (mutation.type === 'config/SET_LOCALE') {
				// trigger changeFormat to get templateUrl base on locale change
				this.changeFormat({ formatId: this.formatId })
			}
		})
	},
	mounted() {
		// if (!this.templateUrl) {
		// 	this.fetchSpreadsheetFormat()
		// }

		// Fetch spreadsheet formats:
		this.fetchAllFormats()
		this.fetchClients()
	},
	methods: {
		...placeOrderMethods,
		...configMethods,
		displayDatetime,
		displayFileSize(bytes) {
			if (!bytes) {
				return ''
			}
			return `${(bytes / 1024).toFixed(2)} KB`
		},
		async handleFileAdded(file) {
			this.uploadSpreadsheet({ file })
		},
		handleSubmit() {
			this.uploadSpreadsheet({ submit: true })
		},
		handleReset() {
			this.reset()
		},
		downloadFeedbackCsv() {
			const element = document.createElement('a')
			element.setAttribute(
				'href',
				'data:text/csv;charset=utf-8,' +
					encodeURIComponent(this.spreadsheetError.feedbackCsv)
			)
			element.setAttribute('download', this.file.name)
			element.style.display = 'none'
			document.body.appendChild(element)
			element.click()
			document.body.removeChild(element)
		},
		handleFormatChange(value) {
			this.changeFormat({ formatId: value })
		},
		handleDeliveryStageChange(value) {
			this.changeDeliveryStage({ deliveryStage: value })
		},
		handleClientChange(value) {
			this.changeClient({ clientId: value })
		},
		handleLocationChange(value) {
			this.changeLocation({ locationId: value })
		},
		getTranslationValue(list, id, key = 'value') {
			const item = list.find(i => i.id === id)
			if (!item || !item.translations) {
				return id
			}
			const translation = item.translations.find(
				t => t.locale === 'en-HK'
			)
			if (!translation) {
				return id
			}
			return translation[key] || id
		}
	}
}
</script>

<style scoped>
.custom-select {
	background-color: #eef2f7;
	color: #313a46;
}

div.dropzone-container {
	position: relative;
}
div.dropzone-overlay {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	border-radius: 6px;
	background: rgba(0, 0, 0, 0.25);
	cursor: not-allowed;
}
</style>
